// FIXED VERSION - Prevents multiple executions
import { Component } from '@theme/component';

class AudioPlayerComponent extends Component {
  wavesurfer = null;
  blockId = '';
  initializationTimeout = null;
  isInitializing = false;
  isDestroyed = false;
  hasInitialized = false; // New flag to track if we've already initialized

  // Global state to prevent multiple instances of the same block
  static instances = new Map();
  static initializationQueue = new Set(); // Prevent queuing duplicate initializations

  connectedCallback() {
    super.connectedCallback();
    this.blockId = this.dataset.blockId || '';
    this.isDestroyed = false;
    
    console.log('🎵 Audio component connected for block:', this.blockId);
    
    // Check for existing instance and return early if found
    const existingInstance = AudioPlayerComponent.instances.get(this.blockId);
    if (existingInstance && existingInstance !== this && existingInstance.isConnected && !existingInstance.isDestroyed) {
      console.log('🔄 Duplicate instance detected, skipping initialization');
      this.isDestroyed = true; // Mark this instance as destroyed
      return; // EARLY RETURN - don't initialize
    }
    
    // Register this instance
    AudioPlayerComponent.instances.set(this.blockId, this);
    
    // Only initialize if we haven't already done so
    if (!this.hasInitialized) {
      this.scheduleInitialization();
    }
  }


  disconnectedCallback() {
    super.disconnectedCallback();
    console.log('🗑️ Audio component disconnected for block:', this.blockId);
    
    this.isDestroyed = true;
    this.clearInitializationTimeout();
    
    // Only cleanup if this is the registered instance
    if (AudioPlayerComponent.instances.get(this.blockId) === this) {
      this.cleanup();
      AudioPlayerComponent.instances.delete(this.blockId);
    }
    
    // Remove from initialization queue
    AudioPlayerComponent.initializationQueue.delete(this.blockId);
  }

  /**
   * Schedule initialization with better debouncing
   */
  scheduleInitialization() {
    // Prevent duplicate scheduling
    if (AudioPlayerComponent.initializationQueue.has(this.blockId)) {
      console.log('⏳ Initialization already queued for block:', this.blockId);
      return;
    }
    
    this.clearInitializationTimeout();
    AudioPlayerComponent.initializationQueue.add(this.blockId);
    
    this.initializationTimeout = setTimeout(() => {
      AudioPlayerComponent.initializationQueue.delete(this.blockId);
      
      if (!this.isDestroyed && this.isConnected && !this.isInitializing && !this.hasInitialized) {
        this.initializeAudio();
      }
    }, 300); // Increased debounce time
  }

  /**
   * Clear any pending initialization
   */
  clearInitializationTimeout() {
    if (this.initializationTimeout) {
      clearTimeout(this.initializationTimeout);
      this.initializationTimeout = null;
    }
  }

  /**
   * Main initialization method with better guards
   */
  async initializeAudio() {
    // Multiple guards to prevent re-initialization
    if (this.isInitializing || this.isDestroyed || !this.isConnected || this.hasInitialized) {
      return;
    }

    this.isInitializing = true;

    try {
      // Find elements
      const audioElement = this.querySelector(`#audio-${this.blockId}`);
      const waveformContainer = this.querySelector(`#waveform-${this.blockId}`);
      const playButton = this.querySelector(`#play-pause-button-${this.blockId}`);

      // Double-check if already initialized via DOM attribute
      if (audioElement?.dataset.audioInitialized === 'true') {
        console.log('✅ Audio already initialized via DOM for block:', this.blockId);
        this.hasInitialized = true;
        return;
      }

      // Check for required elements
      if (!audioElement) {
        console.log('❌ Audio element not found for block:', this.blockId);
        return;
      }

      // For custom controls
      const showControls = this.dataset.showControls === 'true';
      if (!showControls && waveformContainer && playButton) {
        await this.initializeWaveSurfer(audioElement, waveformContainer, playButton);
      }

      // Mark as initialized in multiple ways
      audioElement.dataset.audioInitialized = 'true';
      this.hasInitialized = true;
      
      console.log('🎵 Audio player initialized for block:', this.blockId);

    } catch (error) {
      console.error('❌ Error during audio initialization:', error);
      this.hasInitialized = false; // Reset on error
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Initialize WaveSurfer with better error handling
   */
  async initializeWaveSurfer(audioElement, waveformContainer, playButton) {
    // Wait for WaveSurfer library
    await this.waitForWaveSurfer();

    // Check if component is still valid
    if (!this.isConnected || this.isDestroyed || this.hasInitialized) {
      console.log('🚫 Component state changed during WaveSurfer initialization');
      return;
    }

    // Clean up any existing instance first
    this.cleanup();

    try {
      // Create WaveSurfer instance
      this.wavesurfer = WaveSurfer.create({
        container: waveformContainer,
        waveColor: this.dataset.waveColor || '#00aaff',
        progressColor: this.dataset.progressColor || '#0066cc',
        barWidth: 4,
        barGap: 2,
        barRadius: 2,
        height: 60,
        cursorWidth: 0,
        hideScrollbar: true,
        backend: 'MediaElement',
        media: audioElement
      });

      // Set up event listeners
      this.setupEventListeners(playButton);

      console.log('🌊 WaveSurfer initialized for block:', this.blockId);

    } catch (error) {
      console.error('❌ WaveSurfer initialization error:', error);
      this.hasInitialized = false; // Reset on error
      throw error;
    }
  }

  /**
   * Set up event listeners with better error handling
   */
  setupEventListeners(playButton) {
    if (!this.wavesurfer || this.isDestroyed) return;

    // Play/pause button with debouncing
    let lastClickTime = 0;
    const handlePlayPause = () => {
      const now = Date.now();
      if (now - lastClickTime < 200) return; // 200ms debounce for clicks
      lastClickTime = now;
      
      if (!this.wavesurfer || this.isDestroyed) return;
      
      try {
        this.wavesurfer.playPause();
        this.updatePlayPauseButton(this.wavesurfer.isPlaying(), playButton);
      } catch (error) {
        console.error('Play/pause error:', error);
      }
    };

    playButton.addEventListener('click', handlePlayPause);
    playButton._audioClickHandler = handlePlayPause;

    // WaveSurfer events with error handling and guards
    this.wavesurfer.on('audioprocess', () => {
      if (this.isDestroyed || !this.wavesurfer) return;
      
      try {
        const currentTimeEl = this.querySelector(`#current-time-${this.blockId}`);
        if (currentTimeEl) {
          currentTimeEl.textContent = this.formatTime(this.wavesurfer.getCurrentTime());
        }
      } catch (error) {
        console.error('Time update error:', error);
      }
    });

    this.wavesurfer.on('ready', () => {
      if (this.isDestroyed || !this.wavesurfer) return;
      
      try {
        const durationEl = this.querySelector(`#duration-${this.blockId}`);
        if (durationEl) {
          durationEl.textContent = this.formatTime(this.wavesurfer.getDuration());
        }
        console.log('🎵 WaveSurfer ready for block:', this.blockId);
      } catch (error) {
        console.error('Ready event error:', error);
      }
    });

    this.wavesurfer.on('finish', () => {
      if (this.isDestroyed || !this.wavesurfer) return;
      this.updatePlayPauseButton(false, playButton);
    });

    this.wavesurfer.on('error', (error) => {
      if (error.name === 'AbortError' || this.isDestroyed) {
        return; // Ignore expected errors
      }
      console.error('WaveSurfer error:', error);
    });
  }

  /**
   * Update play/pause button state
   */
  updatePlayPauseButton(isPlaying, playButton) {
    if (!playButton || this.isDestroyed) return;

    try {
      const playIcon = playButton.querySelector('.play-icon');
      const pauseIcon = playButton.querySelector('.pause-icon');

      if (playIcon && pauseIcon) {
        if (isPlaying) {
          playIcon.style.display = 'none';
          pauseIcon.style.display = 'block';
        } else {
          playIcon.style.display = 'block';
          pauseIcon.style.display = 'none';
        }
      }
    } catch (error) {
      console.error('Button update error:', error);
    }
  }

  /**
   * Format time in MM:SS format
   */
  formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return '0:00';
    
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  }

  /**
   * Wait for WaveSurfer library to load
   */
  async waitForWaveSurfer() {
    if (typeof WaveSurfer !== 'undefined') {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 30; // Reduced max attempts

      const checkWaveSurfer = () => {
        if (this.isDestroyed || !this.isConnected) {
          reject(new Error('Component destroyed while waiting for WaveSurfer'));
          return;
        }

        if (typeof WaveSurfer !== 'undefined') {
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error('WaveSurfer failed to load within timeout'));
        } else {
          attempts++;
          setTimeout(checkWaveSurfer, 100);
        }
      };

      checkWaveSurfer();
    });
  }

  /**
   * Clean up WaveSurfer instance and event listeners
   */
  cleanup() {
    // Clean up WaveSurfer
    if (this.wavesurfer) {
      try {
        this.wavesurfer.destroy();
      } catch (error) {
        // Ignore cleanup errors
      }
      this.wavesurfer = null;
    }

    // Clean up event listeners
    const playButton = this.querySelector(`#play-pause-button-${this.blockId}`);
    if (playButton?._audioClickHandler) {
      playButton.removeEventListener('click', playButton._audioClickHandler);
      delete playButton._audioClickHandler;
    }

    // Reset flags
    this.hasInitialized = false;
    
    // Reset initialization flag in DOM
    const audioElement = this.querySelector(`#audio-${this.blockId}`);
    if (audioElement) {
      audioElement.dataset.audioInitialized = 'false';
    }
  }

  /**
   * Static method to get debug info
   */
  static getDebugInfo() {
    return {
      instanceCount: AudioPlayerComponent.instances.size,
      queuedInitializations: AudioPlayerComponent.initializationQueue.size,
      instances: Array.from(AudioPlayerComponent.instances.keys())
    };
  }
}

// Register the component only once
if (!customElements.get('audio-player-component')) {
  customElements.define('audio-player-component', AudioPlayerComponent);
  console.log('🔧 Optimized AudioPlayerComponent registered');

  // Add global debug helpers
  window.AudioPlayerComponent = AudioPlayerComponent;
  
  // Debug helper - run this in console to see what's happening
  window.debugAudio = () => {
    console.log('🐛 Audio Debug Info:', AudioPlayerComponent.getDebugInfo());
  };
  
} else {
  console.log('⚠️ AudioPlayerComponent already registered');
}